import dayjs from 'dayjs';

export type TaskOrder = 'working' | 'due';

export interface OrderableTask {
  title: string;
  status?: string | null;
  priority?: string | null;
  dueDate?: string | null;
  projectId?: string | null;
  projectName?: string | null;
  raci?: {
    responsible?: string[];
    accountable?: string;
    consulted?: string[];
    informed?: string[];
  };
  raciNames?: {
    responsible?: string[];
    accountable?: string;
    consulted?: string[];
    informed?: string[];
  };
}

const PRIORITY_RANK: Record<string, number> = { p1: 0, p2: 1, p3: 2, p4: 3 };

export function isOpenTask(status?: string | null): boolean {
  return status !== 'completed' && status !== 'cancelled';
}

export function taskPeople(task: OrderableTask): Array<{ id: string; name: string }> {
  const raci = task.raci;
  const names = task.raciNames;
  const people: Array<{ id: string; name: string }> = [];
  const push = (id?: string, name?: string) => {
    if (!id) return;
    const label = name && !looksLikeId(name) ? name : '';
    people.push({ id, name: label || name || id });
  };
  (raci?.responsible ?? []).forEach((id, index) => push(id, names?.responsible?.[index]));
  if (raci?.accountable) push(raci.accountable, names?.accountable);
  (raci?.consulted ?? []).forEach((id, index) => push(id, names?.consulted?.[index]));
  (raci?.informed ?? []).forEach((id, index) => push(id, names?.informed?.[index]));
  return people;
}

function looksLikeId(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(value);
}

export function taskProjects(tasks: OrderableTask[]): Array<{ id: string; name: string }> {
  const projects = new Map<string, string>();
  for (const task of tasks) {
    if (!task.projectId || projects.has(task.projectId)) continue;
    projects.set(task.projectId, task.projectName || 'Untitled project');
  }
  return [...projects.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function taskPersonOptions(tasks: OrderableTask[]): Array<{ id: string; name: string }> {
  const people = new Map<string, string>();
  for (const task of tasks) {
    for (const person of taskPeople(task)) {
      if (!people.has(person.id)) people.set(person.id, person.name);
    }
  }
  return [...people.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function taskMatchesPerson(task: OrderableTask, personId: string): boolean {
  if (!personId) return true;
  return taskPeople(task).some((person) => person.id === personId);
}

function dueStamp(due?: string | null): number {
  if (!due) return Number.POSITIVE_INFINITY;
  const parsed = dayjs(String(due).slice(0, 10));
  return parsed.isValid() ? parsed.startOf('day').valueOf() : Number.POSITIVE_INFINITY;
}

function isOverdue(task: OrderableTask): boolean {
  if (!isOpenTask(task.status)) return false;
  const stamp = dueStamp(task.dueDate);
  return Number.isFinite(stamp) && stamp < dayjs().startOf('day').valueOf();
}

export function compareTasks(a: OrderableTask, b: OrderableTask, order: TaskOrder): number {
  if (order === 'due') {
    const byDue = dueStamp(a.dueDate) - dueStamp(b.dueDate);
    if (byDue !== 0) return byDue;
    return a.title.localeCompare(b.title);
  }
  const byOpen = Number(!isOpenTask(a.status)) - Number(!isOpenTask(b.status));
  if (byOpen !== 0) return byOpen;
  const byPriority =
    (PRIORITY_RANK[String(a.priority || '').toLowerCase()] ?? 9) -
    (PRIORITY_RANK[String(b.priority || '').toLowerCase()] ?? 9);
  if (byPriority !== 0) return byPriority;
  const byOverdue = Number(!isOverdue(a)) - Number(!isOverdue(b));
  if (byOverdue !== 0) return byOverdue;
  const byDue = dueStamp(a.dueDate) - dueStamp(b.dueDate);
  if (byDue !== 0) return byDue;
  return a.title.localeCompare(b.title);
}
